import { type Chart } from "chart.js";
import * as React from "react";

import { computedColorOf } from "./seriesColors";

/**
 * Bumps whenever the page's colour scheme changes: the theme provider flips `data-color-scheme`
 * on the root, and the OS preference can change underneath it. Colours are resolved to concrete
 * values when a chart draws, so anything drawn before a switch keeps the old scheme's colours
 * until it is drawn again; depending on this is what triggers that redraw.
 */
export function useColorSchemeVersion(): number {
  const [version, setVersion] = React.useState(0);
  React.useEffect(() => {
    const bump = () => setVersion((current) => current + 1);
    const observer = new MutationObserver(bump);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-color-scheme"],
    });
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    media.addEventListener("change", bump);
    return () => {
      observer.disconnect();
      media.removeEventListener("change", bump);
    };
  }, []);
  return version;
}

/** The parts of a cartesian scale's options that carry colour. Every one of them resolves to an
 *  object through Chart.js's defaults, declared on the chart or not. */
interface ScalePaint {
  border: { color?: string };
  grid: { color?: string };
  ticks: { color?: string };
  title: { color?: string };
}

/**
 * Paints the axes of a cartesian chart — tick labels, axis titles, gridlines and axis lines — in
 * the current scheme's colours.
 *
 * A chart has to be told: Chart.js's own defaults are a single light-only set (`#666` text and a
 * black wash for lines), which reads at 2.8:1 on a dark card and leaves the gridlines invisible,
 * and a canvas cannot follow a CSS variable by itself.
 *
 * Gridlines and axis lines share `--gray-6`, the foundation's border step, which stands off a
 * card at the same weight in both schemes (about 1.4:1). The app's legacy `--border-color` does
 * not: it is a hairline on a light card and nearly twice as heavy on a dark one.
 *
 * Call it when the chart is created and again whenever {@link useColorSchemeVersion} bumps, then
 * `update()` the chart. Not per draw: resolving a CSS variable forces a style recalculation.
 */
export function applyAxisColors(chart: Chart): void {
  const canvas = chart.canvas;
  const text = computedColorOf(canvas, "--gray-11");
  const line = computedColorOf(canvas, "--gray-6");
  for (const axis of ["x", "y"]) {
    // The options type is the union over every scale kind; both of these are cartesian.
    const scale = chart.options.scales?.[axis] as ScalePaint | undefined;
    if (scale) {
      scale.ticks.color = text;
      scale.title.color = text;
      scale.grid.color = line;
      scale.border.color = line;
    }
  }
}
